import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward,
  ArrowUpward,
  FilterListOff,
  Refresh,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { DateInputField } from '../components/DateInputField';
import { TableFilterCell } from '../components/TableFilterCell';
import { enumLabel, statusLabel } from '../i18n/translate';
import { useAuth } from '../contexts/AuthContext';
import { paymentApi } from '../services/api';
import { normalizeInvoiceStatus } from '../types/invoice';
import { PaymentCreateRequest, PaymentInvoice, PaymentMethod, PaymentRecord } from '../types/payment';
import { formatCurrency, formatDate } from '../utils/formatters';
import { getInvoiceBillToInfo } from '../utils/billing';

type ViewMode = 'payments' | 'invoices';

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

const toolbarButtonSx = {
  minWidth: 'auto',
  px: 2,
  py: 0.75,
  textTransform: 'none',
  fontSize: '0.875rem',
};

const compactToolbarButtonSx = {
  minWidth: 'auto',
  px: 1.5,
  py: 0.5,
  textTransform: 'none',
  fontSize: '0.75rem',
  '& .MuiButton-startIcon': {
    marginRight: 0.5,
  },
};

const todayInput = () => dayjs().format('YYYY-MM-DD');
const buildDefaultPaymentForm = (): PaymentCreateRequest => ({
  invoiceIds: [],
  paymentMethod: 'CASH',
  paymentReference: '',
  notes: '',
  paymentDate: todayInput(),
});

export const PaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [viewMode, setViewMode] = useState<ViewMode>('payments');
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(new Set());
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [createSectionOpen, setCreateSectionOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState<PaymentCreateRequest>(buildDefaultPaymentForm);

  const [paymentColumnFilters, setPaymentColumnFilters] = useState({
    paymentNumber: '',
    date: '',
    billTo: '',
    invoiceCount: '',
    amount: '',
    status: '',
    paymentMethod: '',
  });

  const [invoiceColumnFilters, setInvoiceColumnFilters] = useState({
    invoiceNumber: '',
    date: '',
    billTo: '',
    amount: '',
    status: '',
    dueDate: '',
  });

  const { data: payments = [], isLoading: loadingPayments } = useQuery(
    ['payments', companyId],
    () => paymentApi.getAll(),
    { enabled: !!companyId && viewMode === 'payments' }
  );

  const { data: availableInvoices = [], isLoading: loadingAvailableInvoices } = useQuery(
    ['availablePaymentInvoices', companyId],
    () => paymentApi.getAvailableInvoices(),
    { enabled: !!companyId && viewMode === 'invoices' }
  );

  const createPaymentMutation = useMutation(
    (payload: PaymentCreateRequest) => paymentApi.createFromInvoices(payload),
    {
      onSuccess: (createdPayment) => {
        queryClient.invalidateQueries('payments');
        queryClient.invalidateQueries('availablePaymentInvoices');
        toast.success(t('payments.messages.created'));
        setSelectedInvoices(new Set());
        setSelectedPayment(createdPayment);
        setCreateSectionOpen(false);
        setPaymentForm(buildDefaultPaymentForm());
        setViewMode('payments');
        setPage(0);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('payments.messages.createFailed'));
      },
    }
  );

  const getPaymentInvoiceBillToInfo = (invoice: PaymentInvoice) => getInvoiceBillToInfo(invoice);

  const getPaymentBillToInfo = (payment: PaymentRecord) => {
    const firstInvoice = payment.invoices?.[0];
    return firstInvoice ? getPaymentInvoiceBillToInfo(firstInvoice) : {
      key: `payment:${payment.id}`,
      displayName: t('common.unavailable'),
      type: 'UNKNOWN' as const,
    };
  };


  const getInvoiceStatusColor = (status: string) => {
    switch (normalizeInvoiceStatus(status)) {
      case 'PAID':
        return 'success';
      case 'UNPAID':
        return 'warning';
      case 'OVERDUE':
        return 'error';
      case 'SENT':
        return 'info';
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'PROCESSING':
        return 'info';
      case 'FAILED':
        return 'error';
      case 'REFUNDED':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const selectedInvoiceRecords = availableInvoices.filter((invoice) => selectedInvoices.has(invoice.id));

  const selectedBillToKey = (() => {
    const firstSelectedInvoice = selectedInvoiceRecords[0];
    return firstSelectedInvoice ? getPaymentInvoiceBillToInfo(firstSelectedInvoice).key : null;
  })();

  const selectedBillToName = (() => {
    const firstSelectedInvoice = selectedInvoiceRecords[0];
    return firstSelectedInvoice ? getPaymentInvoiceBillToInfo(firstSelectedInvoice).displayName : '';
  })();

  const selectedTotalAmount = selectedInvoiceRecords
    .reduce((sum, invoice) => sum + Number(invoice.balanceDue ?? invoice.totalAmount ?? 0), 0);

  const filteredPayments = payments
    .filter((payment) => {
      const billTo = getPaymentBillToInfo(payment);
      const paymentDateStr = payment.paymentDate ? formatDate(payment.paymentDate).toLowerCase() : '';
      return (
        (!paymentColumnFilters.paymentNumber || (payment.paymentNumber || '').toLowerCase().includes(paymentColumnFilters.paymentNumber.toLowerCase())) &&
        (!paymentColumnFilters.date || paymentDateStr.includes(paymentColumnFilters.date.toLowerCase())) &&
        (!paymentColumnFilters.billTo || billTo.displayName.toLowerCase().includes(paymentColumnFilters.billTo.toLowerCase())) &&
        (!paymentColumnFilters.invoiceCount || String(payment.invoices?.length || 0).includes(paymentColumnFilters.invoiceCount)) &&
        (!paymentColumnFilters.amount || String(payment.amount ?? '').includes(paymentColumnFilters.amount)) &&
        (!paymentColumnFilters.status || (payment.status || '').toLowerCase().includes(paymentColumnFilters.status.toLowerCase())) &&
        (!paymentColumnFilters.paymentMethod || (payment.paymentMethod || '').toLowerCase().includes(paymentColumnFilters.paymentMethod.toLowerCase()))
      );
    })
    .sort((a, b) => {
      if (!sortConfig || viewMode !== 'payments') {
        return 0;
      }

      const aCustomer = getPaymentBillToInfo(a);
      const bCustomer = getPaymentBillToInfo(b);
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortConfig.key) {
        case 'paymentNumber':
          aValue = (a.paymentNumber || '').toLowerCase();
          bValue = (b.paymentNumber || '').toLowerCase();
          break;
        case 'date':
          aValue = a.paymentDate || '';
          bValue = b.paymentDate || '';
          break;
        case 'billTo':
          aValue = aCustomer.displayName.toLowerCase();
          bValue = bCustomer.displayName.toLowerCase();
          break;
        case 'invoiceCount':
          aValue = a.invoices?.length || 0;
          bValue = b.invoices?.length || 0;
          break;
        case 'amount':
          aValue = Number(a.amount || 0);
          bValue = Number(b.amount || 0);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'paymentMethod':
          aValue = (a.paymentMethod || '').toLowerCase();
          bValue = (b.paymentMethod || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const filteredInvoices = availableInvoices
    .filter((invoice) => {
      const customer = getPaymentInvoiceBillToInfo(invoice);
      const invoiceDateStr = invoice.invoiceDate ? formatDate(invoice.invoiceDate).toLowerCase() : '';
      const dueDateStr = invoice.dueDate ? formatDate(invoice.dueDate).toLowerCase() : '';

      return (
        (!invoiceColumnFilters.invoiceNumber || (invoice.invoiceNumber || '').toLowerCase().includes(invoiceColumnFilters.invoiceNumber.toLowerCase())) &&
        (!invoiceColumnFilters.date || invoiceDateStr.includes(invoiceColumnFilters.date.toLowerCase())) &&
        (!invoiceColumnFilters.billTo || customer.displayName.toLowerCase().includes(invoiceColumnFilters.billTo.toLowerCase())) &&
        (!invoiceColumnFilters.amount || String(invoice.balanceDue ?? invoice.totalAmount ?? '').includes(invoiceColumnFilters.amount)) &&
        (!invoiceColumnFilters.status || (invoice.status || '').toLowerCase().includes(invoiceColumnFilters.status.toLowerCase())) &&
        (!invoiceColumnFilters.dueDate || dueDateStr.includes(invoiceColumnFilters.dueDate.toLowerCase()))
      );
    })
    .sort((a, b) => {
      if (!sortConfig || viewMode !== 'invoices') {
        return 0;
      }

      const aCustomer = getPaymentInvoiceBillToInfo(a);
      const bCustomer = getPaymentInvoiceBillToInfo(b);
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortConfig.key) {
        case 'invoiceNumber':
          aValue = (a.invoiceNumber || '').toLowerCase();
          bValue = (b.invoiceNumber || '').toLowerCase();
          break;
        case 'date':
          aValue = a.invoiceDate || '';
          bValue = b.invoiceDate || '';
          break;
        case 'billTo':
          aValue = aCustomer.displayName.toLowerCase();
          bValue = bCustomer.displayName.toLowerCase();
          break;
        case 'amount':
          aValue = Number(a.balanceDue ?? a.totalAmount ?? 0);
          bValue = Number(b.balanceDue ?? b.totalAmount ?? 0);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'dueDate':
          aValue = a.dueDate || '';
          bValue = b.dueDate || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const filteredData = viewMode === 'payments' ? filteredPayments : filteredInvoices;
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  const paginatedInvoiceIds =
    viewMode === 'invoices'
      ? (paginatedData as PaymentInvoice[]).map((invoice) => invoice.id)
      : [];

  const allVisibleInvoicesSelected =
    paginatedInvoiceIds.length > 0 &&
    paginatedInvoiceIds.every((invoiceId) => selectedInvoices.has(invoiceId));

  const someVisibleInvoicesSelected =
    paginatedInvoiceIds.some((invoiceId) => selectedInvoices.has(invoiceId));

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null;
      }
      return { key, direction: 'asc' };
    });
  };

  const handlePaymentFilterChange = (column: keyof typeof paymentColumnFilters, value: string) => {
    setPaymentColumnFilters((prev) => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const handleInvoiceFilterChange = (column: keyof typeof invoiceColumnFilters, value: string) => {
    setInvoiceColumnFilters((prev) => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const handleToggleInvoice = (invoice: PaymentInvoice) => {
    const billTo = getPaymentInvoiceBillToInfo(invoice);
    if (!selectedInvoices.has(invoice.id) && selectedBillToKey && selectedBillToKey !== billTo.key) {
      toast.error(
        t('payments.validation.sameCustomer', {
          defaultValue: 'Selected invoices must belong to the same billing recipient.',
        })
      );
      return;
    }

    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoice.id)) {
      newSelected.delete(invoice.id);
    } else {
      newSelected.add(invoice.id);
    }
    setSelectedInvoices(newSelected);
  };

  const handleToggleVisibleInvoices = () => {
    const newSelected = new Set(selectedInvoices);
    const visibleInvoices = paginatedData as PaymentInvoice[];

    if (allVisibleInvoicesSelected) {
      paginatedInvoiceIds.forEach((invoiceId) => newSelected.delete(invoiceId));
    } else {
      visibleInvoices.forEach((invoice) => {
        const customer = getPaymentInvoiceBillToInfo(invoice);
        if (!selectedBillToKey || customer.key === selectedBillToKey) {
          newSelected.add(invoice.id);
        }
      });
    }

    setSelectedInvoices(newSelected);
  };

  const handleOpenCreateSection = () => {
    if (viewMode !== 'invoices') {
      toast.error(t('payments.validation.switchToInvoices'));
      return;
    }
    if (selectedInvoices.size === 0) {
      toast.error(t('payments.validation.selectInvoice'));
      return;
    }

    setPaymentForm((prev) => ({
      ...prev,
      invoiceIds: Array.from(selectedInvoices),
    }));
    setCreateSectionOpen(true);
  };

  const handleCancelCreateSection = () => {
    setCreateSectionOpen(false);
    setPaymentForm(buildDefaultPaymentForm());
  };

  const handleCreatePayment = () => {
    if (selectedInvoices.size === 0) {
      toast.error(t('payments.validation.selectInvoice'));
      return;
    }

    createPaymentMutation.mutate({
      ...paymentForm,
      invoiceIds: Array.from(selectedInvoices),
    });
  };

  const clearFilters = () => {
    if (viewMode === 'payments') {
      setPaymentColumnFilters({
        paymentNumber: '',
        date: '',
        billTo: '',
        invoiceCount: '',
        amount: '',
        status: '',
        paymentMethod: '',
      });
    } else {
      setInvoiceColumnFilters({
        invoiceNumber: '',
        date: '',
        billTo: '',
        amount: '',
        status: '',
        dueDate: '',
      });
    }
    setSortConfig(null);
    setPage(0);
  };

  const renderFilterCell = (
    placeholder: string,
    value: string,
    onChange: (value: string) => void,
    sortKey: string,
    width: string,
  ) => (
    <TableFilterCell
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onSort={() => handleSort(sortKey)}
      sortDirection={sortConfig?.key === sortKey ? sortConfig.direction : null}
      width={width}
      cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
    />
  );

  const renderStatusChip = (label: string, color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning') => (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        maxWidth: '100%',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        },
      }}
    />
  );

  const startItem = filteredData.length === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = filteredData.length === 0 ? 0 : Math.min((page + 1) * rowsPerPage, filteredData.length);

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar
            sx={{
              pl: { xs: 2, sm: 3 },
              pr: { xs: 2, sm: 3 },
              minHeight: '48px !important',
              py: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              {viewMode === 'invoices' && (
                <Button
                  variant="contained"
                  startIcon={createPaymentMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  onClick={handleOpenCreateSection}
                  disabled={selectedInvoices.size === 0 || createPaymentMutation.isLoading}
                  color="primary"
                  size="small"
                  sx={toolbarButtonSx}
                >
                  {selectedInvoices.size > 0 ? t('payments.newSelected', { count: selectedInvoices.size }) : t('common.new')}
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    onClick={() => {
                      queryClient.invalidateQueries('payments');
                      queryClient.invalidateQueries('availablePaymentInvoices');
                    }}
                    size="small"
                    sx={compactToolbarButtonSx}
                  >
                    {t('common.refresh')}
                  </Button>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '24px' }} />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t('payments.viewMode')}</InputLabel>
                    <Select
                      value={viewMode}
                      label={t('payments.viewMode')}
                      onChange={(e) => {
                        setViewMode(e.target.value as ViewMode);
                        setPage(0);
                        setSelectedPayment(null);
                        setSelectedInvoices(new Set());
                        setCreateSectionOpen(false);
                        setPaymentForm(buildDefaultPaymentForm());
                        setSortConfig(null);
                      }}
                      sx={{ height: '28px', fontSize: '0.8125rem' }}
                    >
                      <MenuItem value="payments">{t('payments.payments')}</MenuItem>
                      <MenuItem value="invoices">{t('payments.availableInvoices')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    disabled={page === 0}
                    sx={{ width: '28px', height: '28px', '&.Mui-disabled': { opacity: 0.3 } }}
                  >
                    <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.8125rem', color: 'text.secondary', minWidth: '80px', textAlign: 'center' }}
                  >
                    {startItem}-{endItem} {t('common.of')} {filteredData.length}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i;
                      } else if (page < 2) {
                        pageNum = i;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 5 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      if (pageNum < 0 || pageNum >= totalPages) {
                        return null;
                      }

                      return (
                        <Button
                          key={pageNum}
                          size="small"
                          onClick={() => setPage(pageNum)}
                          sx={{
                            minWidth: '32px',
                            height: '28px',
                            px: 1,
                            fontSize: '0.8125rem',
                            bgcolor: page === pageNum ? 'primary.main' : 'transparent',
                            color: page === pageNum ? 'white' : 'text.primary',
                            '&:hover': {
                              bgcolor: page === pageNum ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {pageNum + 1}
                        </Button>
                      );
                    })}
                    {totalPages > 5 && page < totalPages - 3 && (
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}>
                        ...
                      </Typography>
                    )}
                    {totalPages > 5 && page < totalPages - 3 && (
                      <Button
                        size="small"
                        onClick={() => setPage(totalPages - 1)}
                        sx={{ minWidth: '32px', height: '28px', px: 1, fontSize: '0.8125rem' }}
                      >
                        {totalPages}
                      </Button>
                    )}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                    sx={{ width: '28px', height: '28px', '&.Mui-disabled': { opacity: 0.3 } }}
                  >
                    <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                  </IconButton>

                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    size="small"
                    sx={{
                      height: '28px',
                      fontSize: '0.8125rem',
                      ml: 1,
                      '& .MuiSelect-select': { py: 0.5, px: 1 },
                    }}
                    renderValue={(value) => t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                  size="small"
                  startIcon={<FilterListOff fontSize="small" />}
                  onClick={clearFilters}
                  sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
                >
                  {t('common.clearFilters')}
                </Button>
              </Toolbar>

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': {
                      fontSize: '0.875rem',
                      px: { xs: 2, sm: 3 },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      {viewMode === 'payments' ? (
                        <>
                          {renderFilterCell(
                            t('fields.paymentNumber'),
                            paymentColumnFilters.paymentNumber,
                            (value) => handlePaymentFilterChange('paymentNumber', value),
                            'paymentNumber',
                            '130px'
                          )}
                          {renderFilterCell(
                            t('fields.paymentDate'),
                            paymentColumnFilters.date,
                            (value) => handlePaymentFilterChange('date', value),
                            'date',
                            '120px'
                          )}
                          {renderFilterCell(
                            t('fields.billTo', 'Bill To'),
                            paymentColumnFilters.billTo,
                            (value) => handlePaymentFilterChange('billTo', value),
                            'billTo',
                            '180px'
                          )}
                          {renderFilterCell(
                            t('fields.invoiceCount'),
                            paymentColumnFilters.invoiceCount,
                            (value) => handlePaymentFilterChange('invoiceCount', value),
                            'invoiceCount',
                            '110px'
                          )}
                          {renderFilterCell(
                            t('fields.amount'),
                            paymentColumnFilters.amount,
                            (value) => handlePaymentFilterChange('amount', value),
                            'amount',
                            '120px'
                          )}
                          {renderFilterCell(
                            t('fields.status'),
                            paymentColumnFilters.status,
                            (value) => handlePaymentFilterChange('status', value),
                            'status',
                            '120px'
                          )}
                          {renderFilterCell(
                            t('fields.paymentMethod'),
                            paymentColumnFilters.paymentMethod,
                            (value) => handlePaymentFilterChange('paymentMethod', value),
                            'paymentMethod',
                            '130px'
                          )}
                        </>
                      ) : (
                        <>
                          <TableCell padding="checkbox" sx={{ py: 0.75, px: 1, width: '48px' }}>
                            <Checkbox
                              checked={allVisibleInvoicesSelected}
                              indeterminate={!allVisibleInvoicesSelected && someVisibleInvoicesSelected}
                              onChange={handleToggleVisibleInvoices}
                              disabled={paginatedInvoiceIds.length === 0}
                            />
                          </TableCell>
                          {renderFilterCell(
                            t('fields.invoiceNumber'),
                            invoiceColumnFilters.invoiceNumber,
                            (value) => handleInvoiceFilterChange('invoiceNumber', value),
                            'invoiceNumber',
                            '130px'
                          )}
                          {renderFilterCell(
                            t('fields.invoiceDate'),
                            invoiceColumnFilters.date,
                            (value) => handleInvoiceFilterChange('date', value),
                            'date',
                            '120px'
                          )}
                          {renderFilterCell(
                            t('fields.billTo', 'Bill To'),
                            invoiceColumnFilters.billTo,
                            (value) => handleInvoiceFilterChange('billTo', value),
                            'billTo',
                            '180px'
                          )}
                          {renderFilterCell(
                            t('fields.amount'),
                            invoiceColumnFilters.amount,
                            (value) => handleInvoiceFilterChange('amount', value),
                            'amount',
                            '120px'
                          )}
                          {renderFilterCell(
                            t('fields.status'),
                            invoiceColumnFilters.status,
                            (value) => handleInvoiceFilterChange('status', value),
                            'status',
                            '120px'
                          )}
                          {renderFilterCell(
                            t('fields.dueDate'),
                            invoiceColumnFilters.dueDate,
                            (value) => handleInvoiceFilterChange('dueDate', value),
                            'dueDate',
                            '120px'
                          )}
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewMode === 'payments' ? (
                      loadingPayments ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : paginatedData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                            {t('payments.empty')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        (paginatedData as PaymentRecord[]).map((payment) => {
                          const customer = getPaymentBillToInfo(payment);
                          return (
                            <TableRow
                              key={payment.id}
                              hover
                              selected={selectedPayment?.id === payment.id}
                              onClick={() => setSelectedPayment(payment)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '130px' }}>{payment.paymentNumber || t('common.unavailable')}</TableCell>
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>{payment.paymentDate ? formatDate(payment.paymentDate) : t('common.unavailable')}</TableCell>
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '180px' }}>
                                {customer.displayName || t('common.unavailable', 'N/A')}
                              </TableCell>
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '110px' }}>{payment.invoices?.length || 0}</TableCell>
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>{formatCurrency(payment.amount)}</TableCell>
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>
                                {renderStatusChip(statusLabel(t, payment.status), getPaymentStatusColor(payment.status) as any)}
                              </TableCell>
                              <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '130px' }}>{enumLabel(t, 'paymentMethods', payment.paymentMethod)}</TableCell>
                            </TableRow>
                          );
                        })
                      )
                    ) : loadingAvailableInvoices ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                          {t('payments.noAvailableInvoices')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      (paginatedData as PaymentInvoice[]).map((invoice) => {
                        const customer = getPaymentInvoiceBillToInfo(invoice);
                        const isSelected = selectedInvoices.has(invoice.id);
                        const isDifferentCustomer =
                          !isSelected &&
                          !!selectedBillToKey &&
                          selectedBillToKey !== customer.key;

                        return (
                          <TableRow
                            key={invoice.id}
                            hover
                            selected={isSelected}
                            sx={{
                              opacity: isDifferentCustomer ? 0.55 : 1,
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ py: 0.75, px: 1, width: '48px' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleInvoice(invoice)}
                                disabled={isDifferentCustomer}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '130px' }}>{invoice.invoiceNumber}</TableCell>
                            <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>{formatDate(invoice.invoiceDate)}</TableCell>
                            <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '180px' }}>
                              {customer.displayName || t('common.unavailable', 'N/A')}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>
                              {formatCurrency(invoice.balanceDue ?? invoice.totalAmount)}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>
                              {renderStatusChip(
                                statusLabel(t, normalizeInvoiceStatus(invoice.status) ?? invoice.status),
                                getInvoiceStatusColor(invoice.status) as any
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 0.75, px: { xs: 2, sm: 3 }, width: '120px' }}>
                              {invoice.dueDate ? formatDate(invoice.dueDate) : t('common.unavailable')}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {createSectionOpen && viewMode === 'invoices' && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    mb: 2,
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  <Box>
                    <Typography variant="h6">{t('payments.createDialogTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('payments.includedInvoices')}: {selectedInvoiceRecords.length}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignSelf: { xs: 'stretch', md: 'auto' } }}>
                    <Button onClick={handleCancelCreateSection}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCreatePayment}
                      disabled={selectedInvoices.size === 0 || createPaymentMutation.isLoading}
                    >
                      {createPaymentMutation.isLoading ? t('common.loading') : t('common.create')}
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert severity={selectedInvoices.size > 0 ? 'info' : 'warning'}>
                      {selectedBillToName
                        ? t('payments.createDialogSummary', {
                            count: selectedInvoices.size,
                            customer: selectedBillToName,
                            total: formatCurrency(selectedTotalAmount),
                          })
                        : t('payments.validation.selectInvoice')}
                    </Alert>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <DateInputField
                      label={t('fields.paymentDate')}
                      value={paymentForm.paymentDate || todayInput()}
                      onChange={(paymentDate) =>
                        setPaymentForm((prev) => ({ ...prev, paymentDate }))
                      }
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>{t('fields.paymentMethod')}</InputLabel>
                      <Select
                        value={paymentForm.paymentMethod}
                        label={t('fields.paymentMethod')}
                        onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      >
                        <MenuItem value="CASH">{enumLabel(t, 'paymentMethods', 'CASH')}</MenuItem>
                        <MenuItem value="CARD">{enumLabel(t, 'paymentMethods', 'CARD')}</MenuItem>
                        <MenuItem value="BANK_TRANSFER">{enumLabel(t, 'paymentMethods', 'BANK_TRANSFER')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label={t('fields.paymentReference')}
                      value={paymentForm.paymentReference || ''}
                      onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentReference: e.target.value }))}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('fields.notes')}
                      value={paymentForm.notes || ''}
                      onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
                      multiline
                      minRows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                    {t('payments.includedInvoices')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('fields.invoiceNumber')}</TableCell>
                          <TableCell>{t('fields.invoiceDate')}</TableCell>
                          <TableCell>{t('fields.dueDate')}</TableCell>
                          <TableCell>{t('fields.billTo', 'Bill To')}</TableCell>
                          <TableCell>{t('fields.status')}</TableCell>
                          <TableCell align="right">{t('fields.amount')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoiceRecords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                              {t('payments.validation.selectInvoice')}
                            </TableCell>
                          </TableRow>
                        ) : (
                          selectedInvoiceRecords.map((invoice) => (
                            <TableRow key={invoice.id}>
                              <TableCell>{invoice.invoiceNumber}</TableCell>
                              <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                              <TableCell>{invoice.dueDate ? formatDate(invoice.dueDate) : t('common.unavailable')}</TableCell>
                              <TableCell>{getPaymentInvoiceBillToInfo(invoice).displayName}</TableCell>
                              <TableCell>
                                {renderStatusChip(
                                  statusLabel(t, normalizeInvoiceStatus(invoice.status) ?? invoice.status),
                                  getInvoiceStatusColor(invoice.status) as any
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(invoice.balanceDue ?? invoice.totalAmount)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>
          )}

          {selectedPayment && viewMode === 'payments' && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{t('payments.details')}</Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.paymentNumber')}</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedPayment.paymentNumber || t('common.unavailable')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.status')}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {renderStatusChip(statusLabel(t, selectedPayment.status), getPaymentStatusColor(selectedPayment.status) as any)}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.paymentDate')}</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedPayment.paymentDate ? formatDate(selectedPayment.paymentDate) : t('common.unavailable')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.paymentMethod')}</Typography>
                    <Typography variant="body1" gutterBottom>
                      {enumLabel(t, 'paymentMethods', selectedPayment.paymentMethod)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.billTo', 'Bill To')}</Typography>
                    <Typography variant="body1" gutterBottom>
                      {getPaymentBillToInfo(selectedPayment).displayName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.invoiceCount')}</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedPayment.invoices?.length || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">{t('fields.totalAmount')}</Typography>
                    <Typography variant="h6" gutterBottom>
                      {formatCurrency(selectedPayment.amount)}
                    </Typography>
                  </Grid>
                  {selectedPayment.transactionReference && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">{t('fields.paymentReference')}</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedPayment.transactionReference}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayment.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">{t('fields.notes')}</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedPayment.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                    {t('payments.includedInvoices')}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('fields.invoiceNumber')}</TableCell>
                          <TableCell>{t('fields.invoiceDate')}</TableCell>
                          <TableCell>{t('fields.billTo', 'Bill To')}</TableCell>
                          <TableCell>{t('fields.status')}</TableCell>
                          <TableCell align="right">{t('fields.totalAmount')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedPayment.invoices || []).map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{invoice.invoiceNumber}</TableCell>
                            <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                            <TableCell>{getPaymentInvoiceBillToInfo(invoice).displayName}</TableCell>
                            <TableCell>
                              {renderStatusChip(
                                statusLabel(t, normalizeInvoiceStatus(invoice.status) ?? invoice.status),
                                getInvoiceStatusColor(invoice.status) as any
                              )}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};
